/* eslint-disable react/no-unused-prop-types,no-console,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import Gravatar from 'react-gravatar';
import ReactTable from 'react-table';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// material-ui icons
import LinearProgress from '@material-ui/core/LinearProgress';
import Refresh from '@material-ui/icons/Refresh';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// our components
import { PAID_AD, CREDIT_BUNDLE } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Datepicker from 'components/common/DatePicker';
import { TableLoader } from 'components/common/TableLoader';
import qs from 'helpers/queryStrings';
import { getUserPhotoUrl } from 'helpers/functions';

/**
 * @class
 * @extends React.Component
 */
class ReportPaidAdList extends React.Component {
  static contextType = RouterContext;

  /**
   * @description
   * Redirect to ads/details route.
   *
   * @param {string} id - Ad id.
   */
  handleDetailsReportAgency = id => {
    const history = this.context;
    const { appliedFilter } = this.props;

    history.push({
      pathname: formatRoute(PAID_AD.REPORT.DETAILS, {
        id,
      }),
      search: qs.stringify({
        startDateGte: appliedFilter.startDateGte,
        startDateLte: appliedFilter.startDateLte,
      }),
    });
  };

  /**
   * @description
   * Redirect to credit_bundle/report route.
   *
   * @param {string} id - Ad id.
   */
  handleCreditBundleReport = id => {
    const history = this.context;
    const { appliedFilter } = this.props;

    history.push({
      pathname: formatRoute(CREDIT_BUNDLE.REPORT, {
        id,
      }),
      search: qs.stringify({
        startDateGte: appliedFilter.startDateGte,
        startDateLte: appliedFilter.startDateLte,
      }),
    });
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @returns {Array} Table columns.
   */
  renderColumns = () => {
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.report.list.headers.agency')}
          </span>
        ),
        accessor: 'agency',
        sortable: false,
        filterable: false,
        minWidth: 200,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.report.list.headers.product')}
          </span>
        ),
        accessor: 'product',
        sortable: false,
        filterable: false,
        minWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.report.list.headers.count')}
          </span>
        ),
        accessor: 'count',
        sortable: false,
        filterable: false,
        minWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.report.list.headers.sales')}
          </span>
        ),
        accessor: 'sales',
        sortable: false,
        filterable: false,
        maxWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.report.list.headers.commission')}
          </span>
        ),
        accessor: 'commission',
        sortable: false,
        filterable: false,
        maxWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'left' }}>
            <p>{t('paidad.report.list.headers.net_sales')}</p>
          </span>
        ),
        accessor: 'net_sales',
        sortable: false,
        filterable: false,
        maxWidth: 150,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} elements - All agency summaries from api.
   * @param {Array} total - Total summaries from api.
   * @returns {Array} Table rows.
   */
  renderRows = (elements, total) => {
    const { classes, t, appliedFilter } = this.props;
    const rows = elements.map(element => ({
      id: element.id,
      agency: (
        <div className={['read', classes.btnAgency].join(' ')}>
          <span key="agency-avatar" className={classes.avatarContainer}>
            {element.agency.photo ? (
              <img
                src={
                  element.agency.fullname === 'Revolico'
                    ? 'assets/img/Logo.svg'
                    : getUserPhotoUrl(element.agency.photo)
                }
                alt="Avatar"
              />
            ) : (
              <Gravatar
                email={element.agency.email || element.agency.fullname}
              />
            )}
          </span>
          <span style={{ marginLeft: '8px' }} key="agency-fullname">
            {element.agency.fullname}
          </span>
        </div>
      ),
      product: (
        <div>
          <a
            data-cy="agencyReportDetails"
            id={`agency-name-${element.agency.id}`}
            className={['read', classes.btnAgency].join(' ')}
            href={`${formatRoute(PAID_AD.REPORT.DETAILS, {
              id: element.agency.id,
            })}?${qs.stringify({
              startDateGte: appliedFilter.startDateGte,
              startDateLte: appliedFilter.startDateLte,
            })}`}
            onClick={e => {
              e.preventDefault();
              this.handleDetailsReportAgency(element.agency.id);
            }}
          >
            <p>{t('paidad.report.list.products.paidad')}</p>
          </a>
          <a
            data-cy="agencyReportDetails"
            id={`agency-name-${element.agency.id}`}
            className={['read', classes.btnAgency].join(' ')}
            href={`${formatRoute(CREDIT_BUNDLE.REPORT, {
              id: element.agency.id,
            })}?${qs.stringify({
              startDateGte: appliedFilter.startDateGte,
              startDateLte: appliedFilter.startDateLte,
            })}`}
            onClick={e => {
              e.preventDefault();
              this.handleCreditBundleReport(element.agency.id);
            }}
          >
            <p>{t('paidad.report.list.products.credit_bundle')}</p>
          </a>
        </div>
      ),
      count: (
        <div>
          <p>
            {
              element.paidAdSummaries.reduce(
                (acc, current) => ({
                  count: current.count + acc.count,
                }),
                { count: 0 },
              ).count
            }
          </p>
          <p>
            {
              element.creditBundleSummaries.reduce(
                (acc, current) => ({
                  count: current.count + acc.count,
                }),
                { count: 0 },
              ).count
            }
          </p>
        </div>
      ),
      sales: (
        <div>
          {element.paidAdSummaries.map(summary => (
            <p
              data-cy="agencySales"
              key={`agency-sales-${summary.currency}-${element.agency.id}`}
            >
              {`${summary.grossSales} ${summary.currency}`}
            </p>
          ))}
          {element.creditBundleSummaries.map(summary => (
            <p
              data-cy="agencySales"
              key={`agency-sales-${summary.currency}-${element.agency.id}`}
            >
              {`${summary.grossSales} ${summary.currency}`}
            </p>
          ))}
        </div>
      ),
      commission: (
        <div>
          {element.agency.fullname === 'Revolico'
            ? null
            : element.paidAdSummaries.map(summary => (
                <p
                  data-cy="agencyCommission"
                  key={`agency-commission-${summary.currency}-${element.agency.id}`}
                >
                  {`${summary.commissions} ${summary.currency}`}
                </p>
              ))}
          {element.agency.fullname === 'Revolico'
            ? null
            : element.creditBundleSummaries.map(summary => (
                <p
                  data-cy="agencyCommission"
                  key={`agency-commission-${summary.currency}-${element.agency.id}`}
                >
                  {`${summary.commissions} ${summary.currency}`}
                </p>
              ))}
        </div>
      ),
      net_sales: (
        <div>
          {element.paidAdSummaries.map(summary => (
            <p
              data-cy="agencyNetSales"
              key={`agency-netsales-${summary.currency}-${element.agency.id}`}
            >
              {`${summary.netSales} ${summary.currency}`}
            </p>
          ))}
          {element.creditBundleSummaries.map(summary => (
            <p
              data-cy="agencyNetSales"
              key={`agency-netsales-${summary.currency}-${element.agency.id}`}
            >
              {`${summary.netSales} ${summary.currency}`}
            </p>
          ))}
        </div>
      ),
    }));
    if (rows.length > 0)
      return [
        ...rows,
        {
          agency: (
            <p style={{ fontWeight: 'bold', padding: '10px' }} id="total-row">
              {t('paidad.report.list.last_row')}
            </p>
          ),
          count: (
            <p style={{ fontWeight: 'bold' }}>
              {total &&
                total.reduce(
                  (acc, current) => ({
                    count: current.count + acc.count,
                  }),
                  { count: 0 },
                ).count}
            </p>
          ),
          sales: (
            <div>
              {total &&
                total.map(summary => (
                  <p style={{ fontWeight: 'bold' }}>
                    {`${summary.grossSales} ${summary.currency}`}
                  </p>
                ))}
            </div>
          ),
          commission: (
            <div>
              {total &&
                total.map(summary => (
                  <p style={{ fontWeight: 'bold' }}>
                    {`${summary.commissions} ${summary.currency}`}
                  </p>
                ))}
            </div>
          ),
          net_sales: (
            <div>
              {total &&
                total.map(summary => (
                  <p style={{ fontWeight: 'bold' }}>
                    {`${summary.netSales} ${summary.currency}`}
                  </p>
                ))}
            </div>
          ),
        },
      ];
    return rows;
  };

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      loading,
      fullLoading,
      classes,
      filter,
      appliedFilter,
      refresh,
      t,
    } = this.props;
    const elements =
      loading || data === undefined ? [] : data.agenciesSummaries.summaries;
    const total =
      loading || data === undefined ? undefined : data.agenciesSummaries.total;
    const rows = this.renderRows(elements, total);
    const cols = this.renderColumns();
    return (
      <React.Fragment>
        <GridContainer>
          <GridItem xs={12}>
            <div className={classes.pageContainer}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem>
                  <h3 className={classes.title}>
                    {t('paidad.report.list.title')}
                  </h3>
                </GridItem>
                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.showFilters}
                    onClick={() => refresh()}
                    id="refresh"
                    key="refresh"
                  >
                    <Refresh />
                    {t('ad.filters.update')}
                  </Button>
                </GridItem>
              </GridContainer>

              <GridContainer>
                <GridItem xs={12}>
                  <div className={classes.contentContainer}>
                    <div
                      className={[
                        classes.loadingLineContainer,
                        classes.topLine,
                      ].join(' ')}
                    >
                      {' '}
                      {fullLoading && (
                        <LinearProgress
                          classes={{
                            barColorPrimary: classes.loadingLine,
                          }}
                        />
                      )}
                    </div>
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={4}>
                        <Datepicker
                          data-cy="paidAdsReportFilterStartDateFrom"
                          title={t(
                            'paidad.report.list.filters.date.from_placeholder',
                          )}
                          defaultValue={t(
                            'paidad.report.list.filters.date.from_placeholder',
                          )}
                          required
                          onChange={date => {
                            filter({
                              startDateGte: moment(date)
                                .startOf('day')
                                .format(),
                            });
                          }}
                          value={
                            appliedFilter.startDateGte !== undefined
                              ? moment(appliedFilter.startDateGte)
                              : ''
                          }
                          validGte={appliedFilter.startDateLte}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={12} md={4}>
                        <Datepicker
                          data-cy="paidAdsReportFilterStartDateTo"
                          title={t(
                            'paidad.report.list.filters.date.to_placeholder',
                          )}
                          defaultValue={t(
                            'paidad.report.list.filters.date.to_placeholder',
                          )}
                          required
                          onChange={date => {
                            filter({
                              startDateLte: moment(date)
                                .endOf('day')
                                .format(),
                            });
                          }}
                          value={
                            appliedFilter.startDateLte !== undefined
                              ? moment(appliedFilter.startDateLte)
                              : ''
                          }
                          validLte={appliedFilter.startDateGte}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={12} md={4}>
                        <div
                          className={`${classes.row} ${classes.userSection}`}
                        >
                          <h6
                            className={classes.title}
                            style={{ width: '100%' }}
                          >
                            {t('paidad.report.list.filters.sales.title')}
                          </h6>
                        </div>
                        <div className={classes.buttonGroup}>
                          <Button
                            color="info"
                            simple={
                              appliedFilter.onlyAgencies === undefined ||
                              appliedFilter.onlyAgencies === false
                            }
                            round
                            className={classes.firstButton}
                            onClick={() =>
                              filter({
                                onlyAgencies: true,
                              })
                            }
                            id="sales-agencies-only"
                            key="sales-agencies-only"
                          >
                            {t('paidad.report.list.filters.sales.only')}
                          </Button>
                          <Button
                            color="info"
                            simple={appliedFilter.onlyAgencies !== undefined}
                            round
                            className={classes.middleButton}
                            onClick={() =>
                              filter({
                                onlyAgencies: undefined,
                              })
                            }
                            id="sales-agencies-include"
                            key="sales-agencies-include"
                          >
                            {t('paidad.report.list.filters.sales.include')}
                          </Button>
                          <Button
                            color="info"
                            round
                            className={classes.lastButton}
                            simple={
                              appliedFilter.onlyAgencies === undefined ||
                              appliedFilter.onlyAgencies === true
                            }
                            onClick={() =>
                              filter({
                                onlyAgencies: false,
                              })
                            }
                            id="sales-agencies-exclude"
                            key="sales-agencies-exclude"
                          >
                            {t('paidad.report.list.filters.sales.exclude')}
                          </Button>
                        </div>
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={12} style={{ marginTop: '20px' }}>
                        <div>
                          {loading ? (
                            <TableLoader />
                          ) : (
                            <ReactTable
                              data={rows}
                              filterable
                              columns={cols}
                              loading={loading}
                              showPagination={false}
                              pageSize={rows.length}
                              className={[
                                '-striped -highlight',
                                classes.tbl,
                              ].join(' ')}
                              defaultFilterMethod={(field, row) => {
                                const id = field.pivotId || field.id;
                                return row[id] !== undefined
                                  ? String(row[id]).startsWith(field.value)
                                  : true;
                              }}
                              noDataText={t('promoted_ad.list.no_data')}
                              minRows={8}
                            />
                          )}
                        </div>
                      </GridItem>
                    </GridContainer>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * PaidAdList PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop ads query's results
 * @param appliedFilter - prop pass to Filter
 * @param loading - prop query or mutations status
 * @param fullLoading - prop query or mutations status
 * @param appliedFilter - prop to know applied filters
 * @callback refresh - callback to refresh page
 * @param t - prop from HOC with string translations
 * @callback filter - prop pass to Filter
 */

ReportPaidAdList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const ReportPaidAdListWrapper = props => <ReportPaidAdList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(ReportPaidAdListWrapper),
);
